import { useMemo, useRef } from 'react'
import { Bar } from 'react-chartjs-2'
import type { ChartData, ChartOptions, Plugin } from 'chart.js'

import { ensureChartJsRegistered, resolveCanvasColor, withCanvasAlpha, chartJsPdfReadyPlugin } from '@/lib/charts/chartjs'
import { chartTooltipPreset } from '../../chart-tooltip-preset'
import { CHART_DEFAULTS } from '../../../../foundations/constants'
import { DECK_CHART_PRIMARY_COLOR, getChartInsideLabelColor } from '../../../../foundations/chart-colors'
import { CHART_LIMITS } from '../../../../foundations/slide-limits'

export interface HorizontalBarDatum {
  label: string
  value: number
}

export interface CriticalZone {
  /** inclusive */
  startIndex: number
  /** inclusive */
  endIndex: number
  label?: string
}

export interface ChartJsHorizontalBarProps {
  data: HorizontalBarDatum[]
  /** One color per bar; falls back to grayscale ramp */
  colors?: string[]
  /** Show value labels inside bars (percent charts only) */
  showValueLabels?: boolean
  /** Controls bar thickness (pixels) */
  barThickness?: number
  /** Chart container height (defaults to 100%) */
  height?: number | string
  /** Auto size height based on bar count */
  autoHeight?: boolean
  /** Extra pixels reserved for axes/labels */
  autoHeightPadding?: number
  /** Disable Chart.js animation (prevents layout jank) */
  disableAnimation?: boolean
  /** X-axis title */
  xAxisTitle?: string
  /** Hide X axis entirely (ticks + grid) */
  showXAxis?: boolean
  /** Hide X-axis title even if axis is shown */
  showXAxisTitle?: boolean
  /** Optional max for X axis */
  xAxisMax?: number
  /** Optional override for X grid visibility */
  showXAxisGrid?: boolean
  /** Max characters per line for y-axis labels (longer labels wrap to 2 lines then truncate) */
  labelMaxLength?: number
  /** Optional bracket annotation for the "critical zone" */
  criticalZone?: CriticalZone
  /** Optional click handler for a specific bar (index in `data`). */
  onBarClick?: (datum: HorizontalBarDatum, index: number) => void
  /** Draw a full-width rounded track behind each bar (like an HTML progress track). */
  showTrack?: boolean
  /** Track fill color (resolved for canvas); only used when `showTrack` is set. */
  trackColor?: string
}

function resolveFontSizePx(value: string, fallbackPx: number) {
  if (typeof window === 'undefined') return fallbackPx
  const el = document.createElement('span')
  el.style.fontSize = value
  el.style.position = 'absolute'
  el.style.visibility = 'hidden'
  document.body.appendChild(el)
  const size = parseFloat(getComputedStyle(el).fontSize || '')
  document.body.removeChild(el)
  return Number.isFinite(size) && size > 0 ? size : fallbackPx
}

function criticalZonePlugin(zone: CriticalZone | undefined) {
  return {
    id: 'criticalZone',
    afterDraw: (chart: any) => {
      if (!zone) return
      const meta = chart?.getDatasetMeta?.(0)
      const bars = meta?.data
      if (!bars || !bars.length) return

      const start = Math.max(0, Math.min(bars.length - 1, zone.startIndex))
      const end = Math.max(0, Math.min(bars.length - 1, zone.endIndex))
      const topIdx = Math.min(start, end)
      const bottomIdx = Math.max(start, end)

      const topBar = bars[topIdx]
      const bottomBar = bars[bottomIdx]
      if (!topBar || !bottomBar) return

      const ctx = chart.ctx
      const right = chart.chartArea?.right
      if (!ctx || typeof right !== 'number') return
      const accentColor = resolveCanvasColor(DECK_CHART_PRIMARY_COLOR, DECK_CHART_PRIMARY_COLOR)
      const textColor = resolveCanvasColor('var(--slide-text-primary)', 'var(--slide-text-primary)')

      // Match the mockup: bracket right of the bars + label
      const x1 = right + 10
      const x2 = right + 20
      const y1 = topBar.y - 20
      const y2 = bottomBar.y + 20

      ctx.save()
      try {
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y1)
        ctx.lineTo(x2, y2)
        ctx.lineTo(x1, y2)
        ctx.strokeStyle = accentColor
        ctx.lineWidth = 3
        ctx.stroke()

        ctx.fillStyle = textColor
        const fontSize = resolveFontSizePx('var(--font-size-caption)', 12)
        ctx.font = `bold ${fontSize}px var(--slide-font-family)`
        ctx.fillText(zone.label || 'CRITICAL ZONE', x2 + 5, (y1 + y2) / 2)
      } catch {
        // defensive
      } finally {
        ctx.restore()
      }
    },
  }
}

// A full-width rounded "track" behind each bar (mirrors an HTML progress-bar track: a muted
// pill the colored fill sits on). Drawn BEFORE the datasets so the bars render on top. Each
// track spans the plot area width at the bar's row, fully rounded to match the bar caps.
function barTrackPlugin(show: boolean, color: string) {
  return {
    id: 'barTrack',
    beforeDatasetsDraw: (chart: any) => {
      if (!show) return
      const meta = chart?.getDatasetMeta?.(0)
      const bars = meta?.data
      const area = chart?.chartArea
      const ctx = chart?.ctx
      if (!bars?.length || !area || !ctx) return
      const fill = resolveCanvasColor(color, color)
      ctx.save()
      try {
        ctx.fillStyle = fill
        for (const bar of bars) {
          const h = Number(bar?.height)
          if (!Number.isFinite(h) || h <= 0) continue
          const y = bar.y - h / 2
          const x = area.left
          const w = area.right - area.left
          if (w <= 0) continue
          const r = Math.min(h / 2, w / 2)
          ctx.beginPath()
          if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(x, y, w, h, r)
          } else {
            ctx.rect(x, y, w, h)
          }
          ctx.fill()
        }
      } catch {
        // defensive — never let a decorative track break the chart
      } finally {
        ctx.restore()
      }
    },
  }
}

/**
 * Wrap a y-axis label into at most 2 lines at word boundaries.
 * If the second line still overflows maxCharsPerLine, truncate with "…".
 * Returns a string[] (Chart.js renders each element as a separate line).
 */
function wrapLabel(label: string, maxCharsPerLine = 20): string | string[] {
  if (label.length <= maxCharsPerLine) return label

  const words = label.split(' ')
  const lines: string[] = []
  let current = ''

  for (const word of words) {
    const next = current ? `${current} ${word}` : word
    if (next.length > maxCharsPerLine && current) {
      lines.push(current)
      current = word
      if (lines.length === 2) break
    } else {
      current = next
    }
  }

  if (current && lines.length < 2) {
    lines.push(current)
  }

  // Truncate the last line if it still overflows
  const last = lines[lines.length - 1]
  if (last && last.length > maxCharsPerLine) {
    lines[lines.length - 1] = last.slice(0, maxCharsPerLine - 1) + '\u2026'
  }

  return lines.length === 1 ? lines[0] : lines
}

export function ChartJsHorizontalBar({
  data,
  colors,
  showValueLabels = true,
  barThickness = CHART_DEFAULTS.barThickness.lg,
  height,
  autoHeight = false,
  autoHeightPadding = 48,
  disableAnimation = false,
  xAxisTitle = 'Score (1–5)',
  showXAxis = true,
  showXAxisTitle = true,
  xAxisMax,
  showXAxisGrid = false,
  labelMaxLength = 20,
  criticalZone,
  onBarClick,
  showTrack = false,
  trackColor = 'hsl(var(--toggle-inactive))',
}: ChartJsHorizontalBarProps) {
  ensureChartJsRegistered()

  const chartRef = useRef<any>(null)

  // Limit data to prevent overflow - use centralized chart limits
  const limitedData = useMemo(() => {
    const maxItems = CHART_LIMITS.horizontalBarMaxItems
    return data.slice(0, maxItems)
  }, [data])

  const resolvedColors = useMemo(() => {
    const n = Math.max(1, limitedData.length)
    if (colors && colors.length) {
      // A single-color array means "one uniform color for every bar" (single-series
      // distribution charts). Repeat it across all bars rather than letting Chart.js
      // fall back to a default fill for the un-colored bars. Multi-color arrays keep
      // their per-bar mapping (categorical charts that intentionally vary per bar).
      if (colors.length === 1) {
        const only = resolveCanvasColor(colors[0])
        return Array.from({ length: n }, () => only)
      }
      return colors.map((c) => resolveCanvasColor(c))
    }
    const primary = resolveCanvasColor(DECK_CHART_PRIMARY_COLOR, DECK_CHART_PRIMARY_COLOR)
    return Array.from({ length: n }, () => primary)
  }, [colors, limitedData.length])

  const chartData = useMemo(() => {
    const barRadius = Math.max(0, Math.round(barThickness / 3))
    const barCornerRadius = {
      topLeft: 0,
      bottomLeft: 0,
      topRight: barRadius,
      bottomRight: barRadius,
    }
    return {
      // Truncate labels to prevent y-axis overflow
      labels: limitedData.map((d) => wrapLabel(d.label, labelMaxLength)),
      datasets: [
        {
          data: limitedData.map((d) => d.value),
          backgroundColor: resolvedColors.map((c) => withCanvasAlpha(c, CHART_DEFAULTS.fillAlpha)),
          borderRadius: barCornerRadius,
          borderSkipped: false,
          borderWidth: 0,
          barThickness,
        },
      ],
    } satisfies ChartData<'bar'>
  }, [limitedData, resolvedColors, barThickness, labelMaxLength])

  const valueLabelPlugin = useMemo(() => {
    if (!showValueLabels) return null
    const isPercentChart = (xAxisTitle || '').includes('%') || xAxisMax === 100
    if (!isPercentChart) return null
    const fontSize = resolveFontSizePx('var(--font-size-caption)', 12)

    return {
      id: 'barValueLabels',
      afterDatasetsDraw: (chart: any) => {
        const ctx = chart?.ctx
        const meta = chart?.getDatasetMeta?.(0)
        const bars = meta?.data
        const values = chart?.data?.datasets?.[0]?.data || []
        if (!ctx || !bars?.length) return

        ctx.save()
        try {
          ctx.font = `600 ${fontSize}px var(--slide-font-family)`
          ctx.textAlign = 'right'
          ctx.textBaseline = 'middle'

          bars.forEach((bar: any, index: number) => {
            const rawValue = Number(values[index])
            if (!Number.isFinite(rawValue)) return
            const rounded = Math.round(rawValue)
            const text = Math.abs(rawValue - rounded) < 0.05 ? `${rounded}%` : `${rawValue.toFixed(1)}%`

            const barWidth = Math.abs(bar.x - bar.base)
            const padding = 6
            const textWidth = ctx.measureText(text).width
            if (barWidth < textWidth + padding * 2) return

            const bgColor = resolvedColors[index % resolvedColors.length]
            ctx.fillStyle = resolveCanvasColor(getChartInsideLabelColor(bgColor), '#ffffff')
            ctx.fillText(text, bar.x - padding, bar.y)
          })
        } catch {
          // defensive
        } finally {
          ctx.restore()
        }
      },
    }
  }, [showValueLabels, xAxisTitle, xAxisMax, resolvedColors])

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      animation: disableAnimation ? false : undefined,
      transitions: disableAnimation
        ? {
            active: { animation: { duration: 0 } },
            resize: { animation: { duration: 0 } },
          }
        : undefined,
      plugins: {
        legend: { display: false },
        tooltip: chartTooltipPreset({
          valueText: (item) => {
            const isPercentChart = (xAxisTitle || '').includes('%') || xAxisMax === 100
            const v = Number(item.parsed?.x)
            if (!Number.isFinite(v)) return ''
            const rounded = Math.round(v)
            const display = Math.abs(v - rounded) < 0.05 ? `${rounded}` : `${v.toFixed(1)}`
            return isPercentChart ? `${display}%` : display
          },
        }),
      },
      layout: { padding: { right: criticalZone ? 90 : 0 } },
      scales: {
        x: {
          display: showXAxis,
          max: xAxisMax,
          grid: { display: showXAxisGrid, color: 'var(--slide-border-soft)' },
          title: {
            display: showXAxis && showXAxisTitle,
            text: xAxisTitle,
            font: { size: CHART_DEFAULTS.tickFontSize.md, weight: 'bold' as const },
            color: 'var(--slide-text-primary)',
          },
          ticks: {
            color: 'var(--slide-text-muted)',
            font: { size: CHART_DEFAULTS.tickFontSize.md },
          },
        },
        y: {
          grid: { display: false },
          ticks: {
            color: 'var(--slide-text-primary)',
            // Every bar must keep its label — Chart.js otherwise auto-skips ticks when
            // wrapped titles eat vertical space (the missing second-version defect).
            autoSkip: false,
            // Use global Chart.js font family so charts match the deck typography.
            font: { size: CHART_DEFAULTS.tickFontSize.lg, weight: 'bold' as const },
          },
        },
      },
      onClick: (evt: any) => {
        if (!onBarClick) return
        const chart = chartRef.current?.getElementsAtEventForMode
          ? chartRef.current
          : chartRef.current?.chart
        const native = evt?.nativeEvent || evt
        const els = chart?.getElementsAtEventForMode?.(native, 'nearest', { intersect: true }, false) || []
        const idx = els?.[0]?.index
        if (typeof idx !== 'number') return
        const datum = limitedData[idx]
        if (!datum) return
        onBarClick(datum, idx)
      },
    }),
    [xAxisTitle, criticalZone, onBarClick, limitedData, disableAnimation, showXAxis, showXAxisTitle, xAxisMax, showXAxisGrid]
  )

  const resolvedHeight = (() => {
    if (autoHeight) {
      const thickness = barThickness
      const gap = Math.max(6, Math.round(thickness * 0.4))
      const base = limitedData.length * (thickness + gap)
      return `${Math.max(120, base + autoHeightPadding)}px`
    }
    if (height === undefined || height === null) return '100%'
    return typeof height === 'number' ? `${height}px` : height
  })()

  const plugins = useMemo<Plugin<'bar', Record<string, unknown>>[]>(() => {
    const items = [
      chartJsPdfReadyPlugin,
      barTrackPlugin(showTrack, trackColor),
      criticalZonePlugin(criticalZone),
      valueLabelPlugin,
    ].filter((plugin) => plugin != null)
    return items as Plugin<'bar', Record<string, unknown>>[]
  }, [criticalZone, valueLabelPlugin, showTrack, trackColor])

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        height: resolvedHeight,
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
      data-chart-library="chartjs"
    >
      <Bar
        ref={chartRef}
        data={chartData}
        options={options}
        plugins={plugins}
      />
    </div>
  )
}
